import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { Button } from "../../buttons/Button";
import {
    DestructiveDialogProps,
    IDestructiveBulkOutcome,
} from "./DestructiveDialog.types";

interface IResult {
    tone: "success" | "warning" | "error";
    title: string;
    message: string;
}

export const DestructiveDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<DestructiveDialogProps>) => {
    const {
        subject,
        mutationFn,
        bulkMutationFn,
        itemId,
        itemIds,
        onSucceeded,
        unconfirmedMessage,
        customDescription,
    } = payload;
    const [unconfirmed, setUnconfirmed] = useState<string | null>(null);

    const { mutateAsync, isPending, isSuccess, isError } = useMutation({
        mutationFn: async (id: number) => {
            return mutationFn?.(id);
        },
    });

    const bulkMutation = useMutation({
        mutationFn: async (ids: number[]) => {
            if (!bulkMutationFn) {
                throw new Error("No bulk deletion was provided");
            }
            return bulkMutationFn(ids);
        },
    });

    const handleDelete = async () => {
        if (bulkMutationFn) {
            bulkMutation.mutate(itemIds, {
                onSuccess: (outcome) => {
                    if (outcome.deletedIds.length > 0) {
                        onSucceeded();
                    }
                },
            });
            return;
        }
        try {
            if (itemId) {
                await mutateAsync(itemId);
                onSucceeded();
            } else if (itemIds) {
                await Promise.all(itemIds.map((id) => mutateAsync(id)));
                onSucceeded();
            }
        } catch (error) {
            const message = unconfirmedMessage?.(error) ?? null;
            if (message !== null) {
                setUnconfirmed(message);
                onSucceeded();
                return;
            }
            const errorMessage = itemId
                ? "Failed to delete item"
                : "Failed to delete items";
            enqueueSnackbar(errorMessage, { variant: "error" });
        }
    };

    const derivedSubject = useMemo(() => {
        if (subject) return subject;
        return itemId ? "item" : "items";
    }, [subject, itemId]);

    const title = `delete ${derivedSubject}`;
    const description =
        customDescription ??
        `Are you sure you want to delete ${itemId ? "this" : "these"} ${derivedSubject}?`;
    const destructiveAction = title;
    const cancelAction = `No, Keep ${itemId ? "It" : "Them"}`;

    const errorMsg = `The ${derivedSubject} could not be deleted. Please try again later.`;
    const successMsg = `${itemId ? "This" : "These"} ${derivedSubject} ${itemId ? "has" : "have"} been deleted.`;

    const bulkResult = ({
        deletedIds,
        failedIds,
        refreshUnconfirmed,
    }: IDestructiveBulkOutcome): IResult => {
        const deleted = deletedIds.length;
        const failed = failedIds.length;
        if (deleted === 0) {
            return { tone: "error", title: "Error", message: errorMsg };
        }
        if (failed > 0) {
            const refreshNote = refreshUnconfirmed
                ? " Some views couldn't refresh."
                : "";
            return {
                tone: "warning",
                title: "Partly deleted",
                message: `${deleted} of ${deleted + failed} ${derivedSubject} ${deleted === 1 ? "was" : "were"} deleted. ${failed} could not be deleted.${refreshNote}`,
            };
        }
        if (refreshUnconfirmed) {
            return {
                tone: "warning",
                title: "Deleted",
                message: `These ${derivedSubject} have been deleted, but some views couldn't refresh.`,
            };
        }
        return { tone: "success", title: "Success", message: successMsg };
    };

    const pending = isPending || bulkMutation.isPending;
    const result: IResult | null = bulkMutation.data
        ? bulkResult(bulkMutation.data)
        : unconfirmed !== null
          ? { tone: "warning", title: "Deleted", message: unconfirmed }
          : isSuccess
            ? { tone: "success", title: "Success", message: successMsg }
            : isError || bulkMutation.isError
              ? { tone: "error", title: "Error", message: errorMsg }
              : null;

    return (
        <Dialog open={open} onClose={() => onClose()}>
            <DialogContent sx={{ p: 5 }}>
                <Stack alignItems={"center"} spacing={6}>
                    {pending && <CircularProgress sx={{ fontSize: 53 }} />}
                    {!pending &&
                        (result === null || result.tone === "error") && (
                            <ErrorOutlineIcon
                                color="error"
                                sx={{ fontSize: 53 }}
                            />
                        )}
                    {result?.tone === "warning" && (
                        <WarningAmberIcon
                            color="warning"
                            sx={{ fontSize: 53 }}
                        />
                    )}
                    {result?.tone === "success" && (
                        <CheckCircleOutlineIcon
                            color="success"
                            sx={{ fontSize: 53 }}
                        />
                    )}
                    <Stack alignItems={"center"}>
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            textTransform={"capitalize"}
                        >
                            {result?.title ?? title}
                        </Typography>
                        <Typography>
                            {result?.message ?? description}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{ px: 5, pb: 5, justifyContent: "center", gap: 3 }}
            >
                {result === null && (
                    <>
                        <Button
                            onClick={() => onClose()}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                color: "text.primary",
                            }}
                            disabled={pending}
                        >
                            {cancelAction}
                        </Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                textTransform: "capitalize",
                            }}
                            disabled={pending}
                        >
                            {destructiveAction}
                        </Button>
                    </>
                )}
                {result !== null && (
                    <Button
                        onClick={() => onClose()}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            px: 2.5,
                            color: "text.primary",
                        }}
                        disabled={pending}
                        fullWidth
                    >
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
